import { z } from 'zod';
import { CanvasNodeSchema, CanvasEdgeSchema } from './canvas.schema';

/**
 * Canvas Page Configuration Schema
 * Page-specific config including viewport (pan/zoom state)
 * NOTE: Viewport is stored per-page, not per-canvas
 */
export const CanvasPageConfigSchema = z.object({
  viewport: z.object({
    x: z.number().default(0),
    y: z.number().default(0),
    zoom: z.number().min(0.1).max(5).default(1)
  }).default({ x: 0, y: 0, zoom: 1 }),

  settings: z.object({
    snapToGrid: z.boolean().default(true),
    gridSize: z.number().int().min(5).max(50).default(20),
    autoSave: z.boolean().default(true),
    theme: z.enum(['light', 'dark']).default('light')
  }).default({
    snapToGrid: true,
    gridSize: 20,
    autoSave: true,
    theme: 'light' as const
  }),

  metadata: z.object({
    version: z.string().default('1.0'),
    lastModified: z.string().default(() => new Date().toISOString()),
    nodeCount: z.number().int().min(0).default(0),
    edgeCount: z.number().int().min(0).default(0)
  }).default({
    version: '1.0',
    lastModified: new Date().toISOString(),
    nodeCount: 0,
    edgeCount: 0
  })
});

/**
 * Canvas Page Creation Schema
 * Used for validating new canvas page creation data
 */
export const CanvasPageCreateSchema = z.object({
  title: z
    .string()
    .min(1, { message: "Page title is required" })
    .max(50, { message: "Page title must be less than 50 characters" })
    .trim(),

  description: z
    .string()
    .max(200, { message: "Description must be less than 200 characters" })
    .optional(),

  order: z
    .number()
    .int()
    .min(0)
    .optional(),

  canvasId: z
    .string()
    .min(1, { message: "Canvas ID is required" })
});

/**
 * Canvas Page Update Schema
 * Used for validating canvas page updates
 */
export const CanvasPageUpdateSchema = z.object({
  title: z
    .string()
    .min(1, { message: "Page title is required" })
    .max(50, { message: "Page title must be less than 50 characters" })
    .trim()
    .optional(),

  description: z
    .string()
    .max(200, { message: "Description must be less than 200 characters" })
    .optional(),

  order: z
    .number()
    .int()
    .min(0)
    .optional(),

  nodes: z
    .array(CanvasNodeSchema)
    .optional(),

  edges: z
    .array(CanvasEdgeSchema)
    .optional(),

  config: CanvasPageConfigSchema
    .optional()
});

/**
 * Canvas Page Reorder Schema
 * Used for validating page reordering within a canvas
 */
export const CanvasPageReorderSchema = z.object({
  pageOrders: z
    .array(z.object({
      uuid: z.string(),
      order: z.number().int().min(0)
    }))
    .min(1, { message: "At least one page order is required" })
});

/**
 * Canvas Page Data Schema
 * Complete page data including nodes, edges, and config (with viewport)
 */
export const CanvasPageDataSchema = z.object({
  nodes: z.array(CanvasNodeSchema).default([]),
  edges: z.array(CanvasEdgeSchema).default([]),
  config: CanvasPageConfigSchema.default({
    viewport: { x: 0, y: 0, zoom: 1 },
    settings: {
      snapToGrid: true,
      gridSize: 20,
      autoSave: true,
      theme: 'light'
    },
    metadata: {
      version: '1.0',
      lastModified: new Date().toISOString(),
      nodeCount: 0,
      edgeCount: 0
    }
  })
});

// TypeScript types inferred from schemas
export type CanvasPageCreateInput = z.infer<typeof CanvasPageCreateSchema>;
export type CanvasPageUpdateInput = z.infer<typeof CanvasPageUpdateSchema>;
export type CanvasPageReorderInput = z.infer<typeof CanvasPageReorderSchema>;
export type CanvasPageData = z.infer<typeof CanvasPageDataSchema>;

// Database model type
export type CanvasPageModel = {
  id: string;
  uuid: string;
  canvasId: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  nodes: any[]; // JSON field
  edges: any[]; // JSON field
  config: any; // JSON field
  order: number;
  createdAt: Date;
  updatedAt: Date;
};

// Canvas page with typed data
export type CanvasPage = Omit<CanvasPageModel, 'nodes' | 'edges' | 'config'> & {
  data: CanvasPageData;
};

// Extended canvas page (includes canvas info)
export type CanvasPageWithCanvas = CanvasPage & {
  canvas: {
    id: string;
    title: string;
    userId: string;
  };
};